/**
 * Semáforo simplificado (5 criterios).
 *
 * ⚪ usos < min_usos
 * 🔴 completitud < completitud_alerta  OR  acierto < acierto_alerta
 * 🔵 acierto > acierto_facil
 * 🟠 acierto < acierto_dificil  (y no es 🔴)
 * 🟢 completitud ≥ completitud_buena  y  acierto entre difícil y fácil
 */

export const DASHBOARD_UMBRALES_DEFAULTS = {
  min_usos: 5,
  completitud_alerta: 75,
  acierto_alerta: 20,
  acierto_facil: 90,
  acierto_dificil: 50,
  completitud_buena: 90,
};

/** Pesos fijos del score (no se configuran en UI). */
const SCORE_FIXED = {
  peso_uso: 25,
  peso_completitud: 25,
  peso_dificultad: 25,
  peso_evidencia: 25,
  usos_cap_uso: 20,
  usos_cap_evidencia: 30,
};

export const ESTADO_META = {
  sin_evidencia: { emoji: '⚪', label: 'Sin evidencia', accion: 'Esperar más datos', color: 'text-slate-600 bg-slate-100' },
  revisar:       { emoji: '🔴', label: 'Revisar',       accion: 'Revisar UX / consigna / scoring', color: 'text-red-700 bg-red-50' },
  facil:         { emoji: '🔵', label: 'Fácil',         accion: 'Hacer versión más difícil', color: 'text-sky-700 bg-sky-50' },
  dificil:       { emoji: '🟠', label: 'Difícil',       accion: 'Mantener como difícil', color: 'text-amber-700 bg-amber-50' },
  buena:         { emoji: '🟢', label: 'Buena',         accion: 'Expandir familia', color: 'text-emerald-700 bg-emerald-50' },
  neutro:        { emoji: '—',  label: 'Sin etiqueta',  accion: 'Seguir observando', color: 'text-slate-500 bg-slate-50' },
};

/** Migra configs viejas (muchos campos) → modelo simple. */
export function normalizeUmbrales(input = {}) {
  const raw = input && typeof input === 'object' ? input : {};
  const pick = (simpleKey, ...legacyKeys) => {
    if (raw[simpleKey] != null && raw[simpleKey] !== '') return Number(raw[simpleKey]);
    for (const k of legacyKeys) {
      if (raw[k] != null && raw[k] !== '') return Number(raw[k]);
    }
    return DASHBOARD_UMBRALES_DEFAULTS[simpleKey];
  };

  let min_usos = pick('min_usos', 'min_usos_sin_evidencia');
  let completitud_alerta = pick('completitud_alerta', 'max_completitud_revisar');
  let acierto_alerta = pick('acierto_alerta', 'max_acierto_revisar');
  let acierto_facil = pick('acierto_facil');
  let acierto_dificil = pick('acierto_dificil');
  let completitud_buena = pick('completitud_buena', 'min_completitud_buena');

  const clampPct = (n) => Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
  min_usos = Math.max(0, Math.round(Number(min_usos) || 0));
  completitud_alerta = clampPct(completitud_alerta);
  acierto_alerta = clampPct(acierto_alerta);
  acierto_facil = clampPct(acierto_facil);
  acierto_dificil = clampPct(acierto_dificil);
  completitud_buena = clampPct(completitud_buena);

  if (acierto_dificil > acierto_facil) {
    [acierto_dificil, acierto_facil] = [acierto_facil, acierto_dificil];
  }

  return {
    min_usos,
    completitud_alerta,
    acierto_alerta,
    acierto_facil,
    acierto_dificil,
    completitud_buena,
  };
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** Orden: ⚪ → 🔴 → 🔵 → 🟠 → 🟢 → neutro */
export function clasificarTarea({ total, pctComp, pctAcierto }, umbrales = DASHBOARD_UMBRALES_DEFAULTS) {
  const u = normalizeUmbrales(umbrales);
  const usos = Number(total) || 0;
  const completitud = Number(pctComp) || 0;
  const acierto = pctAcierto == null ? null : Number(pctAcierto);

  if (usos < u.min_usos) return 'sin_evidencia';

  if (completitud < u.completitud_alerta || (acierto != null && acierto < u.acierto_alerta)) {
    return 'revisar';
  }

  if (acierto != null && acierto > u.acierto_facil) return 'facil';

  if (acierto != null && acierto < u.acierto_dificil) return 'dificil';

  if (
    completitud >= u.completitud_buena
    && acierto != null
    && acierto >= u.acierto_dificil
    && acierto <= u.acierto_facil
  ) {
    return 'buena';
  }

  return 'neutro';
}

export function scoreTarea({ total, pctComp, pctAcierto }, umbrales = DASHBOARD_UMBRALES_DEFAULTS) {
  const u = normalizeUmbrales(umbrales);
  const usos = Number(total) || 0;
  const completitud = clamp(Number(pctComp) || 0, 0, 100);
  const acierto = pctAcierto == null ? null : Number(pctAcierto);

  // Banda óptima = entre difícil y fácil (centro del semáforo)
  const optMin = u.acierto_dificil;
  const optMax = u.acierto_facil;
  let fDif = 50;
  if (acierto != null) {
    if (acierto >= optMin && acierto <= optMax) fDif = 100;
    else if (acierto < optMin) fDif = clamp(Math.round((acierto / Math.max(1, optMin)) * 100), 0, 100);
    else fDif = clamp(Math.round(((100 - acierto) / Math.max(1, 100 - optMax)) * 100), 0, 100);
  }

  const fUso = clamp(usos / SCORE_FIXED.usos_cap_uso, 0, 1) * 100;
  const fEvid = clamp(usos / SCORE_FIXED.usos_cap_evidencia, 0, 1) * 100;
  const score = (
    fUso * SCORE_FIXED.peso_uso
    + completitud * SCORE_FIXED.peso_completitud
    + fDif * SCORE_FIXED.peso_dificultad
    + fEvid * SCORE_FIXED.peso_evidencia
  ) / 100;

  return Math.round(clamp(score, 0, 100));
}

export function enrichDashboardRow(row, umbrales) {
  const pctComp = row.total > 0 ? Math.round(row.completadas / row.total * 100) : 0;
  const pctAcierto = row.total_items > 0 ? Math.round(row.correctas / row.total_items * 100) : null;
  const base = { ...row, pctComp, pctAcierto };
  const estado = clasificarTarea(base, umbrales);
  const score = scoreTarea(base, umbrales);
  return { ...base, estado, score, estadoMeta: ESTADO_META[estado] };
}

export const UMBRALES_FIELD_GROUPS = [
  {
    title: 'Cuándo hay datos suficientes',
    help: 'Con menos usos la tarea queda ⚪ Sin evidencia.',
    fields: [
      { key: 'min_usos', label: 'Usos mínimos', hint: '⚪' },
    ],
  },
  {
    title: 'Alertas (🔴 Revisar)',
    help: 'Si se cumple cualquiera, la tarea pide revisión.',
    fields: [
      { key: 'completitud_alerta', label: 'Completitud menor a (%)', hint: '🔴' },
      { key: 'acierto_alerta', label: 'Acierto menor a (%)', hint: '🔴' },
    ],
  },
  {
    title: 'Dificultad por acierto',
    help: '🔵 por encima del fácil · 🟠 por debajo del difícil · 🟢 en el medio con buena completitud.',
    fields: [
      { key: 'acierto_facil', label: 'Fácil: acierto mayor a (%)', hint: '🔵' },
      { key: 'acierto_dificil', label: 'Difícil: acierto menor a (%)', hint: '🟠' },
      { key: 'completitud_buena', label: 'Buena: completitud mínima (%)', hint: '🟢' },
    ],
  },
];
